import { useCallback, useMemo } from 'react'
import { useDataRead, DataHookOptions, useDataList } from '@hooks/data'
import { getDateFromNow } from '@utils/date-time'

import { normalizeOptionalQueryId } from '@utils/strings'

import { buildVenueDayEventListParams } from '../buildVenueDayEventListParams'
import { eventApi } from './api'
import { venueApi } from '@modules/venue/data'
import { ListParams } from 'types/api'
import { VenueEvent } from 'types/model'
import { InfiniteDataHookOptions, useDataInfinite } from '@hooks/data-infinite'

function getEventPath(append?: number | string) {
  return append ? eventApi.pathPrefix.concat(`${append}`) : eventApi.pathPrefix
}

function getVenuePath(append?: number | string) {
  return append ? venueApi.pathPrefix.concat(`${append}`) : venueApi.pathPrefix
}

export function useEvent(
  event?: string | null,
  options?: DataHookOptions<VenueEvent>
) {
  const id = normalizeOptionalQueryId(event ?? undefined)
  const path = id ? getEventPath(`/${id}`) : null
  return useDataRead<VenueEvent>(eventApi, path, options)
}

export function useVenueEvent(
  venue: string | null,
  event: string | null,
  options?: DataHookOptions<VenueEvent>
) {
  const path = venue && event ? getVenuePath(`/${venue}/events/${event}`) : null
  return useDataRead<VenueEvent>(venueApi, path, options)
}

export function useVenueDayEvent(
  venue: string | null,
  date: string = getDateFromNow().toISOString(),
  options?: DataHookOptions<VenueEvent[]>
) {
  const params = useMemo<ListParams<VenueEvent>>(
    () => buildVenueDayEventListParams(date),
    [date]
  )
  const { data, ...swrResponse } = useVenueEvents(venue, { ...options, params })

  return {
    ...swrResponse,
    data: data?.[0],
  }
}

export function useVenueEvents(
  venue: string | null,
  options?: DataHookOptions<VenueEvent[]>
) {
  const path = venue ? getVenuePath(`/${venue}/events`) : null

  // only calculate fallback arrays on initial render
  const regularFallback = useMemo(() => {
    const fb = options?.config?.fallbackData
    if (!Array.isArray(fb)) return undefined
    return fb.filter((event) => !event.featured)
  }, [])

  const featuredFallback = useMemo(() => {
    const fb = options?.config?.fallbackData
    if (!Array.isArray(fb)) return undefined
    return fb.filter((event) => event.featured)
  }, [])

  const regular = useDataList<VenueEvent>(venueApi, path, {
    ...options,
    config: {
      ...options?.config,
      fallbackData: regularFallback,
    },
  })
  const regularData = regular.data?.map((reg) => ({ ...reg, featured: false }))

  const featured = useDataList<VenueEvent>(venueApi, path, {
    ...options,
    config: {
      ...options?.config,
      fallbackData: featuredFallback,
    },
    params: {
      ...options?.params,
      featured: 'true',
    },
  })
  const featuredData = featured.data

  const fullData = regularData &&
    featuredData && [...featuredData, ...regularData]
  const mutate = useCallback(
    () => Promise.all([regular.mutate(), featured.mutate()]),
    []
  )

  return {
    data: fullData || undefined,
    count: (featured.count ?? 0) + (regular.count ?? 0),
    loading: featured.loading || regular.loading,
    error: featured.error || regular.error,
    isValidating: featured.isValidating || regular.isValidating,
    mutate,
  }
}

export function useInfiniteVenueEvents(
  venue: string | null,
  options: InfiniteDataHookOptions<VenueEvent[]>
) {
  const path = venue ? getVenuePath(`/${venue}/events`) : null

  const featuredEvents = useDataList<VenueEvent>(venueApi, path, {
    params: { featured: 'true' },
  })

  const infinite = useDataInfinite<VenueEvent>(venueApi, path, options)
  const infiniteData = infinite.data?.map((event) => ({
    ...event,
    featured: false,
  }))

  return {
    data:
      featuredEvents.data && infiniteData
        ? [...featuredEvents.data, ...infiniteData]
        : undefined,
    isLoading: featuredEvents.loading || infinite.isLoading,
    isValidating: featuredEvents.isValidating || infinite.isValidating,
    loadMore: infinite.loadMore,
    hasMore: infinite.hasMore,
  }
}
